import React, { useEffect, useState } from 'react';
import {
    FormattedMessage,
    WrappedComponentProps,
    injectIntl,
    useIntl,
} from 'react-intl';
import {
    IFeature,
    IFeatureCountryAccount,
    IFeatureCountryAccountSaveRequest,
} from '../../../models/feature';
import { toast } from 'react-toastify';
import * as featureApi from '../../../api/feature-api';
import CheckboxList from '../../inputs/checkbox-list';
import CheckboxInput from '../../inputs/checkbox-input';
import TextInput from '../../inputs/text-input';
import SelectInput from '../../inputs/select-input';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker-cssmodules.css';
import 'react-datepicker/dist/react-datepicker.css';
import '../../common/react-datepicker.css';

export const ManageFeatureFlags: React.FC = ({}) => {
    const initialFCA = {
        exclude: false,
        isIbd: false,
    } as IFeatureCountryAccount;
    const intl = useIntl();
    const [features, setFeatures] = useState<IFeature[]>([]);
    const [selectedFeatureIds, setSelectedFeatureIds] = useState<string[]>([]);
    const countryCodes = [
        'AU',
        'BR',
        'CA',
        'CO',
        'DE',
        'FR',
        'IT',
        'NZ',
        'UK',
        'US',
    ];
    const [featureCountryAccount, setFeatureCountryAccount] =
        useState<IFeatureCountryAccount>(initialFCA);

    useEffect(() => {
        if (features.length === 0) {
            (async () => {
                featureApi
                    .getFeatures()
                    .then((result) => {
                        setFeatures(result.data ? result.data : []);
                    })
                    .catch(() => {
                        displayError();
                    });
            })();
        }
    }, [features]);

    const handleSelectAll = () => {
        if (features) {
            setSelectedFeatureIds(
                features.map((feature) => feature.id.toString())
            );
        }
    };

    const handleDeselectAll = () => {
        setSelectedFeatureIds([]);
    };

    const handleChange = (e: any) => {
        const { name, checked, value } = e.target;
        setFeatureCountryAccount((prevState) => ({
            ...prevState,
            [name]: name === 'exclude' || name === 'isIbd' ? checked : value,
        }));
    };

    const handleDateChange = (name: string, date: Date | null) => {
        setFeatureCountryAccount((prevState) => ({
            ...prevState,
            [name]: date,
        }));
    };

    const handleFeaturesChanged = (e: any) => {
        setSelectedFeatureIds(e.target.value);
    };

    const handleSaveChanges = (e: any) => {
        const request = {
            featureCountryAccount: {
                id: 0,
                featureId: 0,
                countryCode: featureCountryAccount.countryCode,
                companyAccount: featureCountryAccount.companyAccount,
                activateOn: convertToLocaleTime(
                    featureCountryAccount.activateOn
                ),
                deactivateOn: convertToLocaleTime(
                    featureCountryAccount.deactivateOn
                ),
                exclude: featureCountryAccount.exclude,
                isIbd: featureCountryAccount.isIbd,
            } as IFeatureCountryAccount,
            featureIds: selectedFeatureIds.map((id) => Number(id)),
        } as IFeatureCountryAccountSaveRequest;
        (async () => {
            featureApi
                .saveFeatureCountryAccounts(request)
                .then((result) => {
                    if (result.data) {
                        setFeatureCountryAccount(initialFCA);
                        setSelectedFeatureIds([]);
                        toast.success(
                            intl.formatMessage({ id: 'Changes_Saved' }),
                            {
                                position: 'bottom-right',
                                autoClose: 5000,
                            }
                        );
                    } else {
                        displayError();
                    }
                })
                .catch(() => {
                    displayError();
                });
        })();
        e.preventDefault(); // don't refresh the page
    };

    const convertToLocaleTime = (date: Date | null) => {
        if (date) {
            const localOffset = date.getTimezoneOffset() * 60000;
            const localTime = date.getTime() - localOffset;
            return new Date(localTime);
        } else {
            return null;
        }
    };

    const displayError = () => {
        toast.error('Error Occurred', {
            position: 'bottom-right',
            autoClose: 5000,
        });
    };

    return (
        <>
            {features && (
                <>
                    <div>
                        <h1>
                            {intl.formatMessage({
                                id: 'Manage_Feature_Flags',
                                defaultMessage: 'Manage Feature Flags',
                            })}
                        </h1>
                    </div>
                    <form onSubmit={handleSaveChanges}>
                        <div className="well">
                            <div className="row">
                                <div className="col-md-6">
                                    <div
                                        className="form-horizontal"
                                        role="form"
                                    >
                                        <SelectInput
                                            name="countryCode"
                                            label="Country Code"
                                            options={countryCodes.map(
                                                (countryCode) => ({
                                                    value: countryCode,
                                                    text: countryCode,
                                                })
                                            )}
                                            value={
                                                featureCountryAccount.countryCode
                                            }
                                            hasDefaultOption={true}
                                            defaultOption={'Select'}
                                            onChange={handleChange}
                                        />
                                        <TextInput
                                            name="companyAccount"
                                            label="Company Account"
                                            value={
                                                featureCountryAccount.companyAccount
                                            }
                                            maxLength={20}
                                            onChange={handleChange}
                                        />
                                        <div className="form-group">
                                            <label
                                                className="control-label col-sm-4"
                                                htmlFor="isIbd"
                                            >
                                                {intl.formatMessage({
                                                    id: 'Is_IBD',
                                                    defaultMessage: 'Is IBD',
                                                })}
                                                :
                                            </label>
                                            <div className="field col-sm-8">
                                                <CheckboxInput
                                                    name="isIbd"
                                                    label=""
                                                    checked={
                                                        featureCountryAccount.isIbd
                                                    }
                                                    onChange={handleChange}
                                                />
                                            </div>
                                        </div>
                                        <div className="form-group">
                                            <label
                                                className="control-label col-sm-4"
                                                htmlFor="exclude"
                                            >
                                                {intl.formatMessage({
                                                    id: 'Exclude',
                                                    defaultMessage: 'Exclude',
                                                })}
                                                :
                                            </label>
                                            <div className="field col-sm-8">
                                                <CheckboxInput
                                                    name="exclude"
                                                    label=""
                                                    checked={
                                                        featureCountryAccount.exclude
                                                    }
                                                    onChange={handleChange}
                                                />
                                            </div>
                                        </div>
                                        <div className="form-group">
                                            <label
                                                className="control-label col-sm-4"
                                                htmlFor="activateOn"
                                            >
                                                {intl.formatMessage({
                                                    id: 'Activate_On',
                                                    defaultMessage:
                                                        'Activate On',
                                                })}
                                                :
                                            </label>
                                            <div className="field col-sm-8">
                                                <DatePicker
                                                    selected={
                                                        featureCountryAccount.activateOn
                                                    }
                                                    selectsStart={true}
                                                    startDate={
                                                        featureCountryAccount.activateOn
                                                    }
                                                    endDate={
                                                        featureCountryAccount.deactivateOn
                                                    }
                                                    maxDate={
                                                        featureCountryAccount.deactivateOn
                                                    }
                                                    name="activateOn"
                                                    onChange={(date) => {
                                                        handleDateChange(
                                                            'activateOn',
                                                            date
                                                        );
                                                    }}
                                                    className="form-control"
                                                    autoComplete="off"
                                                    isClearable={true}
                                                    showTimeSelect={true}
                                                />
                                            </div>
                                        </div>
                                        <div className="form-group">
                                            <label
                                                className="control-label col-sm-4"
                                                htmlFor="deactivateOn"
                                            >
                                                {intl.formatMessage({
                                                    id: 'Deactivate_On',
                                                    defaultMessage:
                                                        'Deactivate On',
                                                })}
                                                :
                                            </label>
                                            <div className="field col-sm-8">
                                                <DatePicker
                                                    selected={
                                                        featureCountryAccount.deactivateOn
                                                    }
                                                    selectsEnd={true}
                                                    startDate={
                                                        featureCountryAccount.activateOn
                                                    }
                                                    endDate={
                                                        featureCountryAccount.deactivateOn
                                                    }
                                                    minDate={
                                                        featureCountryAccount.activateOn
                                                    }
                                                    name="deactivateOn"
                                                    onChange={(date) => {
                                                        handleDateChange(
                                                            'deactivateOn',
                                                            date
                                                        );
                                                    }}
                                                    className="form-control"
                                                    autoComplete="off"
                                                    isClearable={true}
                                                    showTimeSelect={true}
                                                />
                                            </div>
                                        </div>
                                        <div className="form-group">
                                            <label
                                                className="control-label col-sm-4"
                                                htmlFor="features"
                                            >
                                                {intl.formatMessage({
                                                    id: 'Features',
                                                    defaultMessage: 'Features',
                                                })}
                                                :
                                            </label>
                                            <div className="field col-sm-8">
                                                <button
                                                    type="button"
                                                    className="btn btn-default"
                                                    onClick={handleSelectAll}
                                                >
                                                    {intl.formatMessage({
                                                        id: 'Select_All',
                                                        defaultMessage:
                                                            'Select All',
                                                    })}
                                                    :
                                                </button>{' '}
                                                <button
                                                    type="button"
                                                    className="btn btn-default"
                                                    onClick={handleDeselectAll}
                                                >
                                                    {intl.formatMessage({
                                                        id: 'Deselect_All',
                                                        defaultMessage:
                                                            'Deselect All',
                                                    })}
                                                    :
                                                </button>
                                                <CheckboxList
                                                    name="features"
                                                    checkedItems={
                                                        selectedFeatureIds
                                                    }
                                                    items={features.map(
                                                        (feature) => ({
                                                            label: feature.featureName,
                                                            name: feature.featureName,
                                                            value: feature.id.toString(),
                                                        })
                                                    )}
                                                    onChange={
                                                        handleFeaturesChanged
                                                    }
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="gutter-top">
                            <button
                                id="save-changes"
                                type="submit"
                                className="btn btn-primary"
                            >
                                <FormattedMessage id="Save_Changes" />
                            </button>
                        </div>
                    </form>
                </>
            )}
        </>
    );
};

export default ManageFeatureFlags;
